import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Feedback';
import { cx } from '@/lib/cx';
import { StatBlock, StatRow } from '@/components/ui/StatBlock';
import { useToast } from '@/components/ui/Toast';
import { groupRoundsApi } from '@/lib/api/endpoints';
import { qk, useCourse, useGroupRound } from '@/lib/api/queries';
import { fmtDecimal, fmtInt } from '@/lib/format/number';
import { fmtDistance, loadUnit } from '@/lib/geo/units';
import { t } from '@/lib/i18n';
import { routes } from '@/routes/paths';
import { TeePicker } from './RecordRound';
import styles from './Record.module.css';
import { ChevronLeftIcon } from '@/components/icons';

/**
 * Entrar numa volta que outra pessoa abriu (canvas 2a).
 *
 * Até aqui o amigo era **uma linha no cartão de alguém**: quem abriu o flight anotava as tacadas
 * dele, e nada disso virava volta dele nem mexia no handicap dele. Entrar troca isso por uma
 * `Round` própria — a mesma que ele teria jogando sozinho —, e daí em diante ele marca putts,
 * fairway, penalidade e bunker como qualquer outra volta.
 *
 * ## Por que esta tela existe, se ela pergunta uma coisa só
 *
 * O tee. Ele decide o course handicap, que distribui as tacadas por stroke index — e o número
 * que ficou guardado quando o dono montou o flight saiu do tee **do dono**, porque era o único
 * conhecido. Entrar sem escolher daria ao amigo o handicap de um tee que ele não jogou, e o net
 * do cartão inteiro sairia errado sem nada na tela indicando isso.
 *
 * ## E os buracos que já passaram
 *
 * Entrar no 12 deixa 11 buracos vazios, e volta incompleta não conta para o handicap. A tela diz
 * isso **antes** do toque, em vez de descobrir depois no resumo: o aviso não bloqueia nada, só
 * evita a surpresa. O preenchimento retroativo é o cartão de sempre — o jogador anda para trás
 * pelos buracos e marca —, não uma segunda tela de recuperação.
 */
export function JoinFlight() {
  const { groupRoundId } = useParams<{ groupRoundId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const unit = loadUnit();

  const group = useGroupRound(groupRoundId);
  const course = useCourse(group.data?.courseId);

  const [tee, setTee] = useState<string | null>(null);
  const [teeBack, setTeeBack] = useState<string | null>(null);
  /**
   * A resposta do 2a: *"estava jogando"* ou *"chegou agora"*. São dois casos que parecem um, e
   * cada um leva a um cartão diferente — um válido para handicap, outro um registro parcial.
   * O padrão é o retroativo, que é o caso que preserva a volta.
   */
  const [catchUp, setCatchUp] = useState(true);
  const [joining, setJoining] = useState(false);

  const courseData = course.data;

  useEffect(() => {
    if (!courseData) return;
    setTee((current) => current ?? courseData.teeBoxes[0]?.name ?? null);
  }, [courseData]);

  const goBack = () => navigate(routes.home);

  if (group.isLoading || course.isLoading || !group.data || !courseData) {
    return (
      <div className={styles.page}>
        <JoinHeader title={t('record.join.title')} onBack={goBack} />
        <div className={styles.body}>
          <Skeleton height={18} width="60%" />
          <Skeleton height={40} style={{ marginTop: 16 }} radius={14} />
          <Skeleton height={40} style={{ marginTop: 8 }} radius={14} />
        </div>
      </div>
    );
  }

  const data = group.data;

  /*
   * Já entrou: a tela não repete a pergunta do tee — a `Round` dele existe e o tee dela está
   * congelado. O caminho daqui é o cartão, que é onde ele ia parar de qualquer jeito.
   */
  if (data.viewer.joined) {
    return (
      <div className={styles.page}>
        <JoinHeader title={t('record.join.title')} onBack={goBack} />
        <div className={styles.body}>
          <p className={styles.joinLead}>{t('record.join.already')}</p>
          <Button block size="lg" onClick={() => navigate(routes.record)}>
            {t('record.join.openScorecard')}
          </Button>
        </div>
      </div>
    );
  }

  // Sem convite não há entrada: o flight foi montado por alguém, e um jogador a mais mudaria o
  // cartão de quem já está jogando. O backend recusa igual; aqui é só não oferecer o botão.
  if (!data.viewer.canJoin) {
    return (
      <div className={styles.page}>
        <JoinHeader title={t('record.join.title')} onBack={goBack} />
        <div className={styles.body}>
          <p className={styles.joinLead}>{t('common.notFound.title')}</p>
        </div>
      </div>
    );
  }

  const ownerName =
    data.participants.find((p) => p.kind === 'owner')?.displayName ?? courseData.name;
  const holesDone = data.standings.find((s) => s.kind === 'owner')?.holesScored ?? 0;

  const needsBackTee = data.holesPlayed > courseData.totalHoles;
  const selectedTee = courseData.teeBoxes.find((box) => box.name === tee);
  const canJoin = Boolean(tee) && (!needsBackTee || Boolean(teeBack)) && !joining;

  const join = async () => {
    if (!tee || !groupRoundId) return;
    setJoining(true);
    try {
      await groupRoundsApi.join(groupRoundId, {
        teeBoxName: tee,
        teeBoxNameBack: needsBackTee && teeBack ? teeBack : null,
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: qk.allRounds }),
        queryClient.invalidateQueries({ queryKey: qk.groupRound(groupRoundId) }),
        queryClient.invalidateQueries({ queryKey: ['feed'] }),
      ]);
      /*
       * `replace` porque voltar para cá depois de entrar cairia na tela de "você já está nesta
       * volta" — um beco. O cartão é o passo seguinte, e o `RecordRound` já sabe retomar uma
       * volta em andamento e descobrir sozinho que ela é de flight.
       */
      /*
       * `replace` porque voltar para cá depois de entrar cairia na tela de "você já está nesta
       * volta" — um beco. O cartão é o passo seguinte, e o `RecordRound` já sabe retomar uma
       * volta em andamento e descobrir sozinho que ela é de flight.
       *
       * Quem disse que chegou agora abre direto no buraco corrente; quem vai preencher desde o
       * início abre no 1, que é o padrão do cartão.
       */
      navigate(routes.record, {
        replace: true,
        state: catchUp ? undefined : { startAtHole: holesDone + 1 },
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('record.join.failed'));
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className={styles.page}>
      <JoinHeader title={courseData.name} onBack={goBack} />

      <div className={styles.body}>
        <p className={styles.joinLead}>
          {t('record.join.who', { name: ownerName })}
          <span className={styles.joinWhere}>
            {holesDone > 0
              ? t('record.join.where', {
                  course: courseData.name,
                  holes: data.holesPlayed,
                  hole: holesDone + 1,
                })
              : t('record.join.whereFresh', {
                  course: courseData.name,
                  holes: data.holesPlayed,
                })}
          </span>
        </p>

        {/*
          A pergunta do canvas 2a, e ela é a tela: separa *estava jogando* de *chegou agora*, que
          parecem o mesmo caso e não são. A consequência para o handicap fica escrita em cada
          opção **antes** do toque — depois de finalizada, a volta não pode ser editada.
        */}
        {holesDone > 0 && (
          <fieldset className={styles.joinChoice}>
            <legend className={styles.groupLabel}>
              {t('record.join.behindQuestion', { last: holesDone })}
            </legend>
            <JoinOption
              selected={catchUp}
              title={t('record.join.fromStart')}
              note={t('record.join.fromStartNote', { count: holesDone })}
              onSelect={() => setCatchUp(true)}
            />
            <JoinOption
              selected={!catchUp}
              title={t('record.join.fromHere', { hole: holesDone + 1 })}
              note={t('record.join.fromHereNote', { count: data.holesPlayed - holesDone })}
              onSelect={() => setCatchUp(false)}
            />
          </fieldset>
        )}

        <div className={styles.groupLabel}>
          {needsBackTee ? t('record.join.firstNine') : t('record.join.tee')}
        </div>
        <TeePicker teeBoxes={courseData.teeBoxes} value={tee} onChange={setTee} />

        {needsBackTee && (
          <>
            <div className={styles.groupLabel}>{t('record.join.secondNine')}</div>
            <TeePicker teeBoxes={courseData.teeBoxes} value={teeBack} onChange={setTeeBack} />
          </>
        )}

        <p className={styles.joinWhy}>{t('record.join.teeWhy')}</p>

        {selectedTee && (
          <StatRow className={styles.teeFacts}>
            <StatBlock
              label={t('scorecard.distance')}
              value={fmtDistance(selectedTee.totalDistance, unit)}
            />
            <StatBlock
              label={t('round.courseRating')}
              value={fmtDecimal(selectedTee.courseRating)}
            />
            <StatBlock label={t('round.slope')} value={fmtInt(selectedTee.slopeRating)} />
          </StatRow>
        )}

        <div style={{ marginTop: '1.25rem' }}>
          <Button block size="lg" disabled={!canJoin} onClick={() => void join()}>
            {joining
              ? t('record.join.joining')
              : holesDone > 0 && catchUp
                ? t('record.join.confirmCatchUp', { last: holesDone })
                : t('record.join.confirm')}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Uma das duas respostas. Rádio de verdade, para o teclado e o leitor de tela andarem entre elas. */
function JoinOption({
  selected,
  title,
  note,
  onSelect,
}: {
  selected: boolean;
  title: string;
  note: string;
  onSelect: () => void;
}) {
  return (
    <label className={cx(styles.joinOption, selected && styles.joinOptionOn)}>
      <input
        type="radio"
        name="join-catch-up"
        className={styles.joinRadio}
        checked={selected}
        onChange={onSelect}
      />
      <span>
        <span className={styles.joinOptionTitle}>{title}</span>
        <span className={styles.joinOptionNote}>{note}</span>
      </span>
    </label>
  );
}

function JoinHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <header className={styles.header}>
      <div className={styles.headerTop}>
        <button
          type="button"
          className={styles.headerIcon}
          onClick={onBack}
          aria-label={t('record.join.back')}
        >
          <ChevronLeftIcon size={22} />
        </button>
        <span className={styles.stepLabel}>{t('record.join.title')}</span>
        <span aria-hidden="true" style={{ width: 'var(--tap)' }} />
      </div>
      <h1 className={styles.headerTitle}>{title}</h1>
    </header>
  );
}
